import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { BasketItem, Discount } from '../../../../types/basket';
import { LOADING_TYPES, useCheckoutState } from '../../../../utils/checkoutState';
import { config } from '../../../../utils/config';
import FormattedCurrency from '../../../atoms/FormattedCurrency';
import LoadingIndicator from '../../../atoms/LoadingIndicator';
import Price from '../../../atoms/Price';
import ItemDiscounts from '../ItemDiscounts';
import Quantity from '../Quantity';
import UnitPrice from '../UnitPrice';
import './style.scss';

type ItemTotalsProps = {
  item: BasketItem;
  isLoading: string;
};

export const getDiscounts = (discountComponents: Omit<Discount, 'total'>) =>
  Object.entries(discountComponents)
    .filter(
      ([key, value]) =>
        !!value && (config.basket.showNetPrices ? key.includes('WithoutTax') : !key.includes('WithoutTax'))
    )
    .map(([key, value]) => ({ key: key.replace('WithoutTax', ''), value }));

const ItemTotals = ({ item, isLoading }: ItemTotalsProps) => {
  const { checkoutState } = useCheckoutState();
  const { total, ...discountComponents } = item.discount;
  const { costWithoutTaxWithDiscount } = item.total;
  const { disableQuantityChange, showNetPrices, replaceFreeItemPriceWithText } = config.basket;

  const totalWithDiscounts = showNetPrices ? costWithoutTaxWithDiscount : item.total.costWithTaxWithDiscount;

  const discounts = getDiscounts(discountComponents);
  const isDiscounted = !!discounts?.length;

  const locationSearch = new URLSearchParams(window.location.search);
  const isPaypalReviewPage = !!locationSearch.get('isReviewPage');

  const isQuantityChangeDisabled =
    disableQuantityChange || item.isQuantityChangeDisabled || isPaypalReviewPage || checkoutState.isExpressCheckout;

  const showLowestPriorPrice = config.features.lowestPriorPrice.enabled && !!total && !!item.lowestPriorPrice?.withTax;

  return (
    <div className="item-totals">
      <div className="item-totals__section item-totals__section--quantity-and-price">
        <div>
          {item.reference && <UnitPrice {...item.reference} />}
          {isQuantityChangeDisabled ?
            <>
              <FormattedMessage id="basket.item.quantity" />: {item.quantity}
            </>
          : <Quantity item={item} />}
        </div>
        <div className="item-totals__loading-container">
          <LoadingIndicator
            isSmall
            contentClassName={classNames('item-totals__loading', {
              'item-totals__loading--visible': isLoading === LOADING_TYPES.itemQuantityChange(item.id)
            })}
          />
          <div
            className={classNames('item-totals__price', {
              'item-totals__price--loading': isLoading === LOADING_TYPES.itemQuantityChange(item.id)
            })}
            data-test-id="item-totals-price"
          >
            <ItemDiscounts item={item} />
            {replaceFreeItemPriceWithText && !totalWithDiscounts ?
              <FormattedMessage id="basket.item.freeItemText" />
            : <Price price={totalWithDiscounts} isDiscounted={isDiscounted} dataTestId="price" />}
          </div>
        </div>
      </div>
      {showLowestPriorPrice && (
        <p className="item-totals__lowest-prior-price">
          <FormattedMessage id="basket.item.lowestPriorPrice" />:
          <span className="item-totals__lowest-prior-value" data-test-id="price-lowest-prior">
            <FormattedCurrency value={item.lowestPriorPrice!.withTax} />
          </span>
        </p>
      )}
    </div>
  );
};

export default ItemTotals;
